import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';

import { DropdownMenuItem } from 'src/common/DropdownMenu/DropdownMenuItem';
import type {
  DispatchTripRead,
  OverrideStatusEnum,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import type {
  TripListRead,
} from 'src/common/external/bambi-api/tripsApi';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import { useTripMenuActions } from '../DispatchCalendar/useTripMenuActions';

interface Props {
  trip: DispatchTripRead | TripRead | TripListRead;
}

const disabledStatuses = [
  'transporting',
  'at dropoff',
  'completed',
] as OverrideStatusEnum[];
export function CancellationMenu({ trip }: Props) {
  if (trip.status === 'canceled') {
    // No intermediaries currently support uncancelling
    return trip.intermediary_id || trip.intermediary ? (
      <DisabledUncancelMenuItem />
    ) : (
      <UncancelMenuItem tripId={trip.id} />
    );
  }

  // Disabling cancel menu item swallows hover events so we wrap CancelMenuItem
  // to preserve styles and have the tooltip
  return disabledStatuses.includes(trip.status) ? (
    <SimpleTooltip
      trigger={
        <div>
          <CancelMenuItem tripId={trip.id} disabled />
        </div>
      }
      content={`Trips in ${trip.status} status cannot be cancelled. If you need to cancel, change the status to at pickup first.`}
    />
  ) : (
    <CancelMenuItem tripId={trip.id} />
  );
}

function CancelMenuItem({
  tripId,
  disabled,
}: {
  tripId: string;
  disabled?: boolean;
}) {
  const menuActions = useTripMenuActions();

  return (
    <DropdownMenuItem
      onClick={() => {
        menuActions.cancel(tripId);
      }}
      disabled={disabled}
    >
      Cancel
    </DropdownMenuItem>
  );
}

function UncancelMenuItem({ tripId }: { tripId: string }) {
  const { isRideOrderingPortalUser } = useUserRoles();
  const menuActions = useTripMenuActions();

  if (isRideOrderingPortalUser) {
    return null;
  }

  return (
    <DropdownMenuItem onClick={() => menuActions.uncancel(tripId)}>
      Uncancel
    </DropdownMenuItem>
  );
}

function DisabledUncancelMenuItem() {
  return (
    <DropdownMenuItem>
      <SimpleTooltip
        trigger={<span className="disabled text-slate-400">Uncancel</span>}
        content="This trip is not able to be uncancelled due to Broker restrictions"
      />
    </DropdownMenuItem>
  );
}
